import React, { useState, useEffect } from "react";
import { withAuthenticationRequired, useAuth0 } from "@auth0/auth0-react";
import { useHistory, useParams } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, Alert, Table, Form, FormGroup, Label, Input, Row, Col } from "reactstrap";
import Loading from "../components/Loading";
import RoleBadge from "../components/RoleBadge";
import { getOrganisation, addOrganisationMember, removeOrganisationMember, assignMemberRole, removeMemberRole } from "../utils/apiService";
import { canManageMembers } from "../utils/orgRoles";
import { ROLE_TYPES } from "../utils/orgConstants";

const OrganisationMembersComponent = () => {
  const { id } = useParams();
  const history = useHistory();
  const { getAccessTokenSilently } = useAuth0();

  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [addForm, setAddForm] = useState({
    emailAddress: "",
    firstName: "",
    lastName: "",
  });
  const [addingMember, setAddingMember] = useState(false);

  useEffect(() => {
    loadOrganisation();
  }, [id]);

  const loadOrganisation = async () => {
    setLoading(true);
    setError("");
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const data = await getOrganisation(id, token);

      const role = data?.userRole || data?.role || null;
      setUserRole(role);

      if (!canManageMembers(role)) {
        history.push(`/organisations/${id}`);
        return;
      }

      setOrg({
        id: data?.id || id,
        name: data?.name || data?.orgName || 'Unknown',
      });

      const membersList = data?.members || [];
      setMembers(membersList);
    } catch (err) {
      setError(err?.message || "Failed to load organisation");
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    setError("");
    setAddingMember(true);
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      await addOrganisationMember(id, addForm, token);
      setAddForm({ emailAddress: "", firstName: "", lastName: "" });
      await loadOrganisation();
    } catch (err) {
      setError(err?.message || "Failed to add member");
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName}?`)) {
      return;
    }

    setError("");
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      await removeOrganisationMember(id, memberId, token);
      await loadOrganisation();
    } catch (err) {
      setError(err?.message || "Failed to remove member");
    }
  };

  const handleRoleChange = async (memberId, newRoleId, currentRoleId) => {
    setError("");
    try {
      const token = await getAccessTokenSilently().catch(() => null);

      // Remove current role if exists
      if (currentRoleId) {
        await removeMemberRole(id, memberId, currentRoleId, token);
      }

      // Assign new role
      await assignMemberRole(id, memberId, newRoleId, token);
      await loadOrganisation();
    } catch (err) {
      setError(err?.message || "Failed to change member role");
    }
  };

  const isOnlyOwner = (member) => {
    const ownerCount = members.filter(m => m.role === 'Owner').length;
    return member.role === 'Owner' && ownerCount === 1;
  };

  if (loading) {
    return <Loading />;
  }

  if (!canManageMembers(userRole)) {
    return (
      <Alert color="warning">
        You do not have permission to manage members.
        <div className="mt-3">
          <Button color="secondary" onClick={() => history.push(`/organisations/${id}`)}>
            Back to Details
          </Button>
        </div>
      </Alert>
    );
  }

  if (!org) {
    return <Alert color="warning">Organisation not found</Alert>;
  }

  return (
    <div>
      {error && <Alert color="danger">{error}</Alert>}

      <Card className="mb-4">
        <CardHeader>
          <h1 className="mb-0">Manage Members - {org.name}</h1>
        </CardHeader>
        <CardBody>
          <Button color="secondary" size="sm" onClick={() => history.push(`/organisations/${id}`)}>
            <i className="fas fa-arrow-left me-2"></i>Back to Organisation
          </Button>
        </CardBody>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <h5 className="mb-0">Add Member</h5>
        </CardHeader>
        <CardBody>
          <Form onSubmit={handleAddMember}>
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label for="emailAddress">Email Address *</Label>
                  <Input
                    id="emailAddress"
                    name="emailAddress"
                    type="email"
                    value={addForm.emailAddress}
                    onChange={(e) => setAddForm(prev => ({ ...prev, emailAddress: e.target.value }))}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label for="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={addForm.firstName}
                    onChange={(e) => setAddForm(prev => ({ ...prev, firstName: e.target.value }))}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label for="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={addForm.lastName}
                    onChange={(e) => setAddForm(prev => ({ ...prev, lastName: e.target.value }))}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <FormGroup className="w-100">
                  <Button color="primary" type="submit" block disabled={addingMember}>
                    {addingMember ? 'Adding...' : 'Add'}
                  </Button>
                </FormGroup>
              </Col>
            </Row>
          </Form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h5 className="mb-0">Members ({members.length})</h5>
        </CardHeader>
        <CardBody>
          {members.length === 0 ? (
            <p className="text-muted">No members yet. Add the first member above.</p>
          ) : (
            <Table striped responsive>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => {
                  const memberName = `${member.firstName || ''} ${member.lastName || ''}`.trim();
                  const onlyOwner = isOnlyOwner(member);

                  return (
                    <tr key={member.id || member.memberId}>
                      <td>{memberName}</td>
                      <td>{member.email || member.emailAddress}</td>
                      <td>
                        <RoleBadge role={member.role} />
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Input
                            type="select"
                            size="sm"
                            style={{ width: '150px' }}
                            value={member.roleId || ''}
                            onChange={(e) => handleRoleChange(member.id || member.memberId, parseInt(e.target.value), member.roleId)}
                          >
                            <option value="">Select Role</option>
                            <option value="1">Owner</option>
                            <option value="2">Admin</option>
                            <option value="3">Member</option>
                          </Input>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => handleRemoveMember(member.id || member.memberId, memberName)}
                            disabled={onlyOwner}
                            title={onlyOwner ? 'Cannot remove the only owner' : 'Remove member'}
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default withAuthenticationRequired(OrganisationMembersComponent, {
  onRedirecting: () => <Loading />,
});
